import { useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import {
  useCompanyTeam,
  useInvitePartnerUser,
  useDeactivatePartnerUser,
  type CompanyTeamMember,
} from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';
import { PARTNER_INVITE_ROLE_OPTIONS } from '@/config/partnerOptions';

/**
 * /company/team — the Partner-side admin's "Users & Roles" page. Previously labelled "Team";
 * copy now uses "Users & Roles" per the ledger. URL kept as /company/team to avoid breaking
 * bookmarks. Invite user + remove user flows land here so partner_admin can grow their team
 * without going through the TPO.
 */

const PARTNER_ROLE_LABELS: Record<string, string> = {
  partner_admin: 'Partner admin',
  recruiter: 'Recruiter',
  hiring_manager: 'Hiring manager',
  technical_panel: 'Technical panel',
  partner_accountant: 'Accountant',
};

function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase();
}

export function CompanyTeamPage() {
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  const partnerId = currentUser?.partner ?? undefined;
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'recruiter', fullName: '' });
  const [inviteLinkBanner, setInviteLinkBanner] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useCompanyTeam({ q: q || undefined, role: role || undefined });
  const inviteUser = useInvitePartnerUser('group');
  const deactivateUser = useDeactivatePartnerUser('group');
  const members: CompanyTeamMember[] = data?.members ?? [];
  const partnerName = data?.partner?.name ?? 'your organisation';

  const roleCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of members) map.set(m.role, (map.get(m.role) ?? 0) + 1);
    return map;
  }, [members]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Users &amp; Roles</h1>
          <p className="subhead">
            Everyone who signs in as {partnerName}. Recruiters, hiring managers, panels — all here.
          </p>
        </div>
        <div className="ph-actions">
          <button
            className="btn btn-primary"
            data-testid="invite-user-btn"
            onClick={() => {
              if (!partnerId) {
                toast.show({ tone: 'warning', message: "Couldn't identify your partner — please sign in again." });
                return;
              }
              setInviteOpen(true);
            }}
          >
            <Icon name="user-plus" size={14} />Invite user
          </button>
        </div>
      </div>

      <div className="filterbar">
        <div className="filterbar-search">
          <Icon name="search" size={14} className="text-ink-tertiary" />
          <input
            className="input"
            type="search"
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {Object.entries(PARTNER_ROLE_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}{roleCounts.get(k) ? ` (${roleCounts.get(k)})` : ''}</option>
          ))}
        </select>
      </div>

      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && members.length === 0}
        emptyTitle="No teammates yet"
        emptyHint="It's just you on this workspace. Invite recruiters, hiring managers, and interview panels so Kollez usage scales with your team."
        onRetry={() => void refetch()}
      >
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th className="hide-mobile">Last active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m._id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="avatar">{initials(m.fullName)}</span>
                        <div>
                          <div className="cell-title">{m.fullName}</div>
                          <div className="cell-sub">{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="badge">{PARTNER_ROLE_LABELS[m.role] ?? m.role}</span></td>
                    <td className="hide-mobile cell-sub">
                      {m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td>
                      {m._id !== currentUser?.id && partnerId && (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={async () => {
                            if (!confirm(`Remove ${m.fullName}?`)) return;
                            try {
                              await deactivateUser.mutateAsync({ partnerId, userId: m._id });
                              toast.show({ message: `${m.fullName} removed.` });
                              void refetch();
                            } catch (err) {
                              toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't remove." });
                            }
                          }}
                        >Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncBoundary>

      <p className="helper">
        Roles determine what each user can do. Only <b>Partner admin</b> can invite others and sign MoUs.
        Recruiters + hiring managers post openings and review candidates. Technical panels see only their
        assigned interview rounds.
      </p>

      <Drawer open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite user" size="md">
        <div className="grid gap-4">
          <p className="helper m-0">They'll get an invite link to activate their Kollez account and join your workspace.</p>
          <label className="grid gap-1.5"><span className="label">Full name</span>
            <input className="input" value={inviteForm.fullName} onChange={(e) => setInviteForm((f) => ({ ...f, fullName: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Email *</span>
            <input className="input" type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <label className="grid gap-1.5"><span className="label">Role *</span>
            <select className="select" value={inviteForm.role} onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}>
              {PARTNER_INVITE_ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setInviteOpen(false)} disabled={inviteUser.isPending}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled={inviteUser.isPending || !inviteForm.email.trim() || !partnerId}
            onClick={() => {
              if (!partnerId) return;
              inviteUser.mutate({
                partnerId,
                body: {
                  email: inviteForm.email.trim().toLowerCase(),
                  role: inviteForm.role,
                  fullName: inviteForm.fullName.trim() || undefined,
                },
              }, {
                onSuccess: (res) => {
                  toast.show({ message: 'Invite generated.' });
                  setInviteLinkBanner(res.inviteLink);
                  setInviteOpen(false);
                  setInviteForm({ email: '', role: 'recruiter', fullName: '' });
                },
                onError: (err) => toast.show({ tone: 'danger', message: err instanceof Error ? err.message : "Couldn't invite." }),
              });
            }}
          >{inviteUser.isPending ? 'Sending…' : 'Send invite'}</button>
        </div>
      </Drawer>

      {inviteLinkBanner && (
        <div className="card p-4" data-testid="invite-link-block">
          <h3 className="text-sm font-semibold m-0 mb-1">Invite link (test only)</h3>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: 6, background: 'rgb(var(--bg-tertiary))', borderRadius: 6 }}>
            {inviteLinkBanner}
          </code>
          <button className="btn btn-outline mt-2" onClick={() => setInviteLinkBanner(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
